import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/useAuthStore";
import { X, Pencil, Send, RefreshCw, Loader2, Server, Receipt, Maximize2 } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../lib/axios";
import NoteDialog from "../ui/NoteDialog";
import ErpConnectionBanner from "../erp/ErpConnectionBanner";
import { useErpConnectionStore } from "../../store/useErpConnectionStore";

interface Props {
  project: any;
  onClose: () => void;
  onEdit?: () => void;
}

const money = (n: any) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");
const dateStr = (d: any) => (d ? new Date(d).toLocaleDateString() : "—");

const Row = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400">{label}</div>
    {/* Long identifiers (ERP ids) wrap instead of running into the next column. */}
    <div className={`mt-0.5 text-gray-900 ${mono ? "text-[11px] font-mono break-all leading-snug" : "text-[13px]"}`}>
      {value ?? "—"}
    </div>
  </div>
);

// Read-only project detail — the "View" action from the projects list.
const ProjectViewModal = ({ project, onClose, onEdit }: Props) => {
  const navigate = useNavigate();
  const { authUser } = useAuthStore();
  // Milestone financials live in the Invoice module — jump straight to the record.
  const viewInvoice = (inv: any) => {
    const base = authUser?.role === "Business Developer" ? "/bd/invoices" : "/admin/invoices";
    navigate(`${base}?search=${encodeURIComponent(inv.invoiceNumber)}`);
  };
  const contactName =
    typeof project.assignedContact === "object" ? project.assignedContact?.name : null;

  // CRE → ERP submission (see ERP-INTEGRATION-CONTRACT.md).
  const [erp, setErp] = useState<any>(project.erp || null);
  const [sync, setSync] = useState<string>(project.sync?.status || "");
  const [erpBusy, setErpBusy] = useState(false);
  // Keep ERP status/sync in sync with fresh prop data (live socket refetch).
  useEffect(() => {
    setErp(project.erp || null);
    setSync(project.sync?.status || "");
  }, [project.erp?.status, project.erp?.progress, project.sync?.status]);
  // Milestone invoices — nothing is auto-generated any more: the BD invoices
  // each unpaid milestone explicitly, which also emails the client a pay link.
  const [invoices, setInvoices] = useState<any[]>([]);
  const [invBusy, setInvBusy] = useState<number | null>(null);
  const loadInvoices = async () => {
    try {
      const { data } = await axiosInstance.get(`/invoices?project=${project._id}`);
      setInvoices(data.invoices || data || []);
    } catch { /* list is a convenience — ignore */ }
  };
  useEffect(() => { loadInvoices(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [project._id]);
  const invoiceForMilestone = (i: number) =>
    invoices.find((inv: any) => inv.milestoneIndex === i && !inv.isDeleted);
  const generateInvoice = async (i: number) => {
    setInvBusy(i);
    try {
      const { data } = await axiosInstance.post("/invoices/milestone", { projectId: project._id, milestoneIndex: i });
      toast.success(data.emailed
        ? `Invoice ${data.invoice.invoiceNumber} created — payment link emailed to the client.`
        : `Invoice ${data.invoice.invoiceNumber} created. No client email on file, so no link was sent.`);
      await loadInvoices();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't generate the invoice");
    } finally {
      setInvBusy(null);
    }
  };

  // Project requirements the BD acknowledges once obtained AND verified.
  const [checklist, setChecklist] = useState<any[]>(project.requirementChecklist || []);
  const [ackBusy, setAckBusy] = useState<number | null>(null);
  useEffect(() => { setChecklist(project.requirementChecklist || []); }, [project._id]);
  // Notes are collected in an in-app dialog (never a browser prompt).
  const [dialog, setDialog] = useState<
    | null
    | {
        kind: "checklist" | "milestone";
        index: number;
        title: string;
        description: string;
        placeholder: string;
        confirmLabel: string;
        tone: "primary" | "danger";
      }
  >(null);

  const acknowledgeItem = async (i: number, acknowledged: boolean, note = "") => {
    // Re-submitting after a rejection needs a note explaining the fix — ask for
    // it in the dialog, then run this again with the answer.
    if (acknowledged && checklist[i]?.status === "Rejected" && !note) {
      setDialog({
        kind: "checklist",
        index: i,
        title: "What did you fix?",
        description: `"${checklist[i].label}" was rejected: ${checklist[i].rejectionNote || "no reason given"}. Describe the fix — it goes to the Project Manager with the re-submission.`,
        placeholder: "e.g. New admin login created and tested on the staging site.",
        confirmLabel: "Send for review",
        tone: "primary",
      });
      return;
    }
    setAckBusy(i);
    try {
      const { data } = await axiosInstance.post(
        `/project/${project._id}/checklist/${i}/acknowledge`,
        { acknowledged, note }
      );
      setChecklist((prev) => prev.map((c, idx) => (idx === i ? data.item : c)));
      toast.success(data.message);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't record the acknowledgment");
    } finally {
      setAckBusy(null);
    }
  };

  // BD review of an ERP-completed milestone. Acknowledging releases the
  // deliverables for client delivery; rejecting requires a note that goes back
  // to the Project Manager in ERP.
  const [reviewBusy, setReviewBusy] = useState<number | null>(null);
  const reviewMilestone = async (i: number, action: "acknowledge" | "reject", note = "") => {
    if (action === "reject" && !note) {
      setDialog({
        kind: "milestone",
        index: i,
        title: "Why is this milestone being rejected?",
        description: `"${project.milestones?.[i]?.title || "This milestone"}" goes back to the Project Manager with your note, and the milestone returns to In Progress.`,
        placeholder: "e.g. Footer is missing the social links and newsletter signup.",
        confirmLabel: "Reject and send back",
        tone: "danger",
      });
      return;
    }
    setReviewBusy(i);
    try {
      const { data } = await axiosInstance.post(
        `/integrations/erp/projects/${project._id}/milestones/${i}/${action}`,
        { note }
      );
      toast.success(
        action === "acknowledge"
          ? "Milestone acknowledged — deliverables are ready for the client."
          : "Milestone rejected and sent back to the Project Manager."
      );
      if (project.milestones?.[i]) {
        project.milestones[i].acknowledgement = data.milestone?.acknowledgement || project.milestones[i].acknowledgement;
        project.milestones[i].lifecycleStatus = data.milestone?.lifecycleStatus || project.milestones[i].lifecycleStatus;
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't record the review");
    } finally {
      setReviewBusy(null);
    }
  };

  // Retry a decision whose delivery to ERP failed — the decision itself is
  // already recorded, only the push is repeated.
  const resendReview = async (i: number) => {
    setReviewBusy(i);
    try {
      const { data } = await axiosInstance.post(
        `/integrations/erp/projects/${project._id}/milestones/${i}/resend`
      );
      toast[data.erpNotified ? "success" : "error"](data.message);
      if (project.milestones?.[i] && data.milestone) project.milestones[i] = data.milestone;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't resend the decision");
    } finally {
      setReviewBusy(null);
    }
  };

  const checkErpConnection = useErpConnectionStore((s) => s.check);
  // The button is disabled while the connection is known to be untrustworthy,
  // so the refusal is visible before the click rather than after it.
  const erpBlocked = useErpConnectionStore(
    (s) => s.connection !== null && !s.connection.canSubmit
  );

  const submitToErp = async () => {
    setErpBusy(true);
    try {
      // Checked here as well as on the server. The server is what actually
      // enforces it; this is so the user is told before they wait on a request
      // that was never going to land.
      const live = await checkErpConnection({ force: true });
      if (live && !live.canSubmit) {
        toast.error(
          live.state === "Not Configured"
            ? "ERP is not configured on this environment."
            : "ERP is unavailable — nothing has been sent. Your project is saved."
        );
        setErpBusy(false);
        return;
      }

      const { data } = await axiosInstance.post(`/integrations/erp/projects/${project._id}/submit`);
      if (data.erpProjectId) setErp({ ...(erp || {}), projectId: data.erpProjectId, status: "Pending" });
      setSync(data.sync || "Synced");
      toast.success(
        data.recovered
          ? data.message
          : data.alreadyLinked
          ? "Already linked to ERP."
          : "Submitted to ERP."
      );
    } catch (e: any) {
      const d = e?.response?.data;
      // Three different outcomes that used to look identical to the user, and
      // which call for three different things from them.
      if (d?.blocked) {
        // Nothing was sent. Nothing is lost.
        setSync(d.sync || "Held — ERP Unavailable");
        toast.error(d.message, { duration: 7000 });
      } else if (d?.uncertain) {
        // It may have landed. Retrying is still safe, because the server asks
        // ERP what it holds before sending again.
        setSync(d.sync || "Sync Uncertain");
        toast.error(`${d.message} ${d.detail}`, { duration: 9000 });
      } else {
        setSync("Sync Failed");
        toast.error(d?.message || "ERP submit failed");
      }
    } finally {
      setErpBusy(false);
    }
  };

  const statusClass =
    project.projectStatus === "Completed"
      ? "bg-emerald-100 text-emerald-700"
      : project.projectStatus === "In Progress"
      ? "bg-blue-100 text-blue-700"
      : project.projectStatus === "Cancelled"
      ? "bg-red-100 text-red-700"
      : "bg-gray-100 text-gray-700";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{project.name}</h2>
            <div className="flex items-center gap-2 mt-1.5">
              {project.isDraft && (
                <span className="inline-flex px-2.5 py-0.5 text-[11px] font-bold rounded-full bg-amber-100 text-amber-700 border border-amber-200" title="Saved draft — finish project creation to activate it">
                  DRAFT
                </span>
              )}
              <span className={`inline-flex px-2.5 py-0.5 text-[11px] font-bold rounded-full ${statusClass}`}>
                {project.projectStatus || "—"}
              </span>
              <span className="text-[12px] text-gray-500">{project.projectType || "—"}{project.projectSubType ? ` · ${project.projectSubType}` : ""}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* This modal is a summary. Documents, questionnaire answers, both
                milestone sets in full and the ERP payload live on the full
                record — without a way through, they may as well not exist. */}
            <button
              onClick={() => navigate(`/projects/${project._id}`)}
              title="Open the full record"
              className="px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-[12px] font-semibold inline-flex items-center gap-1.5"
            >
              <Maximize2 size={14} /> Full record
            </button>
            {onEdit && (
              <button onClick={onEdit} title="Edit" className="p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700">
                <Pencil size={16} />
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><X size={18} /></button>
          </div>
        </div>

        <div className="overflow-y-auto cre-scroll px-6 py-5 space-y-5">
          {/* Description */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">Project Description</div>
            {project.description ? (
              <p className="text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap">{project.description}</p>
            ) : (
              <p className="text-[13px] text-gray-400 italic">No description provided.</p>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2 border-t border-gray-100">
            <Row label="Assigned Contact" value={contactName} />
            <Row label="Supervisor" value={project?.supervisor?.fullName} />
            <Row label="Subcategory" value={project.projectSubType || project.designType} />
            <Row label="Billing Type" value={project.billingType} />
            <Row label="Estimated Hours" value={project.estimatedHours || "—"} />
            <Row label="ETA" value={dateStr(project.eta)} />
            <Row label="Source" value={project.source} />
            <Row label="Payment Status" value={project.paymentStatus} />
            <Row label="Project Value" value={money(project.projectPrice)} />
            <Row label="Escrow Amount" value={money(project.escrowAmount)} />
            <Row label="Released Payment" value={money(project.releasedPayment)} />
          </div>

          {/* Project requirements — each one is acknowledged by the BD once it
              is actually received AND verified, and that record goes to ERP. */}
          {Array.isArray(project.requirementChecklist) && project.requirementChecklist.length > 0 && (
            <div className="pt-2 border-t border-gray-100">
              <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2">
                Project Requirements ({checklist.filter((c: any) => c.acknowledged).length}/{checklist.length} acknowledged)
              </div>
              {checklist.some((c: any) => c.status === "Rejected") && (
                <div className="mb-2 rounded-lg border-2 border-red-300 bg-red-50 px-3 py-2.5">
                  <p className="text-[12px] font-bold text-red-800">
                    The Project Manager rejected{" "}
                    {checklist.filter((c: any) => c.status === "Rejected").length} requirement(s).
                  </p>
                  <p className="text-[11px] text-red-700/80 mt-0.5">
                    Fix what was raised, then press Acknowledge again — it goes straight back to ERP for review.
                  </p>
                </div>
              )}
              <div className="space-y-1.5">
                {checklist.map((c: any, i: number) => (
                  <div
                    key={i}
                    className={`flex items-start justify-between gap-2 text-[12px] rounded-lg px-3 py-2 ${
                      c.status === "Rejected" ? "bg-red-50 border border-red-300" : "bg-gray-50"
                    }`}
                  >
                    <div className="min-w-0">
                      <span className="text-gray-800 font-medium">
                        {c.label}
                        {c.required !== false && <span className="text-red-500"> *</span>}
                        {c.category && (
                          <span className="ml-1.5 px-1.5 py-px rounded-full text-[10px] font-bold bg-slate-100 text-slate-500">{c.category}</span>
                        )}
                      </span>
                      {c.detail && <span className="block text-[11px] text-gray-500 mt-0.5">{c.detail}</span>}
                      {c.acknowledged && c.status !== "Rejected" && (
                        <span className="block text-[11px] text-emerald-600 mt-0.5">
                          ✓ Acknowledged by {c.acknowledgedByName || "BD"}
                          {c.acknowledgedAt ? ` · ${new Date(c.acknowledgedAt).toLocaleDateString()}` : ""}
                        </span>
                      )}
                      {/* ERP's verdict on what the BD acknowledged. */}
                      {c.erpReview === "Approved" && (
                        <span className="block text-[11px] text-blue-600 mt-0.5">
                          ✓ Approved by {c.erpReviewedBy || "Project Manager"}
                          {c.erpReviewedAt ? ` · ${new Date(c.erpReviewedAt).toLocaleDateString()}` : ""}
                        </span>
                      )}
                      {c.status === "Rejected" && (
                        <span className="block text-[11px] text-red-700 font-semibold mt-1">
                          ✕ Rejected by {c.rejectedBy || c.erpReviewedBy || "Project Manager"}
                          {c.rejectedAt ? ` · ${new Date(c.rejectedAt).toLocaleDateString()}` : ""}
                          {c.rejectionNote ? ` — ${c.rejectionNote}` : ""}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => acknowledgeItem(i, c.status === "Rejected" ? true : !c.acknowledged)}
                      disabled={ackBusy === i}
                      className={`shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-semibold disabled:opacity-60 ${
                        c.status === "Rejected"
                          ? "bg-red-600 text-white hover:brightness-110"
                          : c.acknowledged
                          ? "border border-gray-200 text-gray-500 hover:bg-gray-100"
                          : "bg-emerald-600 text-white hover:brightness-110"
                      }`}
                      title={
                        c.status === "Rejected"
                          ? "Fixed — send it back to the Project Manager for review"
                          : c.acknowledged
                          ? "Remove the acknowledgment"
                          : "Confirm you have received and verified this"
                      }
                    >
                      {c.status === "Rejected" ? "Fixed — re-submit" : c.acknowledged ? "Undo" : "Acknowledge"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(project.milestones) && project.milestones.length > 0 && (
            <div className="pt-2 border-t border-gray-100">
              <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2">Milestones</div>
              <div className="space-y-1.5">
                {project.milestones.map((m: any, i: number) => {
                  const inv = invoiceForMilestone(i);
                  const settled = ["Released", "Completed"].includes(m.status);
                  // The BD can review whenever ERP has delivered something: a
                  // completed/under-review milestone, or one with submitted
                  // work. A decision can also be changed — a rejection that
                  // never reached ERP must not strand the milestone.
                  const decided = ["Acknowledged", "Rejected"].includes(m.acknowledgement?.status);
                  const awaitingReview =
                    ["Completed", "Under Review"].includes(m.lifecycleStatus) ||
                    m.deliverySubmissions?.length > 0 ||
                    decided;
                  // The decision was made but ERP never confirmed it (it was
                  // down, or the decision predates delivery tracking) — offer to
                  // send it again rather than leaving it silently unsynced.
                  const undelivered =
                    ["Acknowledged", "Rejected"].includes(m.acknowledgement?.status) &&
                    m.acknowledgement?.erpNotified !== true;
                  return (
                    <div key={i} className="flex items-center justify-between gap-2 text-[12px] bg-gray-50 rounded-lg px-3 py-2 flex-wrap">
                      <span className="text-gray-700">
                        {m.title || `Milestone ${i + 1}`}
                        {/* The two independent states, always both shown so the
                            difference is obvious: work (ERP) and payment (CRE). */}
                        <span className="ml-2 px-1.5 py-px rounded-full text-[10px] font-bold bg-blue-50 text-blue-700" title="Work status — managed by the delivery team in ERP">
                          {m.lifecycleStatus || "Backlog / Not Started"}
                        </span>
                        <span className="ml-1 px-1.5 py-px rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700" title="Payment status — follows this milestone's invoice">
                          {m.paymentState || "Pending"}
                        </span>
                        {m.acknowledgement?.status === "Acknowledged" && (
                          <span className="ml-1 px-1.5 py-px rounded-full text-[10px] font-bold bg-blue-50 text-blue-700" title={`Acknowledged by ${m.acknowledgement.byName || "BD"}`}>
                            ✓ {m.acknowledgement.byName || "Acknowledged"}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-3 flex-wrap">
                        {undelivered && (
                          <button
                            onClick={() => resendReview(i)}
                            disabled={reviewBusy === i}
                            className="px-2.5 py-1 rounded-lg bg-amber-500 text-white text-[11px] font-semibold hover:brightness-110 disabled:opacity-60"
                            title={`Your ${m.acknowledgement.status.toLowerCase()} never reached ERP (${m.acknowledgement.erpError || "ERP unreachable"}). Send it again.`}
                          >
                            ⟳ Resend to ERP
                          </button>
                        )}
                        {awaitingReview && (
                          <span className="flex items-center gap-1.5">
                            <button
                              onClick={() => reviewMilestone(i, "acknowledge")}
                              disabled={reviewBusy === i}
                              className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-[11px] font-semibold hover:brightness-110 disabled:opacity-60"
                              title="Accept the completed milestone and release its deliverables for the client"
                            >
                              {m.acknowledgement?.status === "Acknowledged" ? "Acknowledge again" : "Acknowledge"}
                            </button>
                            <button
                              onClick={() => reviewMilestone(i, "reject")}
                              disabled={reviewBusy === i}
                              className="px-2.5 py-1 rounded-lg border border-red-200 text-red-600 text-[11px] font-semibold hover:bg-red-50 disabled:opacity-60"
                              title="Send it back to the Project Manager with a note"
                            >
                              {m.acknowledgement?.status === "Rejected" ? "Reject again" : "Reject"}
                            </button>
                          </span>
                        )}
                        <span className="font-semibold text-gray-800">{money(m.amount)}</span>
                        {inv ? (
                          <button
                            onClick={() => viewInvoice(inv)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            title={`View Invoice ${inv.invoiceNumber} · ${inv.status} — opens the Invoice module`}
                          >
                            <Receipt size={11} /> View Invoice · {inv.invoiceNumber}
                          </button>
                        ) : settled || project.isDraft ? null : (
                          <button
                            onClick={() => generateInvoice(i)}
                            disabled={invBusy === i}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-600 text-white text-[11px] font-semibold hover:brightness-110 disabled:opacity-60"
                            title="Create the invoice for this milestone and email the client their payment link"
                          >
                            {invBusy === i ? <Loader2 size={11} className="animate-spin" /> : <Receipt size={11} />}
                            Generate Invoice
                          </button>
                        )}
                      </span>

                      {/* Deliverables checklist — ticked off by the Project
                          Manager in ERP, with whoever completed each one, plus
                          the files submitted for each revision. */}
                      {(m.deliverables?.length > 0 || m.deliverySubmissions?.length > 0 || m.rejectionHistory?.length > 0) && (
                        <div className="w-full mt-2 pt-2 border-t border-gray-200 space-y-2">
                          {m.deliverables?.length > 0 && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">
                                Deliverables ({m.deliverables.filter((d: any) => d.done).length}/{m.deliverables.length} completed)
                              </div>
                              <div className="space-y-1">
                                {m.deliverables.map((d: any, di: number) => (
                                  <div key={di} className="flex items-start gap-2 text-[12px]">
                                    <span className={d.done ? "text-emerald-600" : "text-gray-300"}>{d.done ? "☑" : "☐"}</span>
                                    <span className={d.done ? "text-gray-700" : "text-gray-500"}>
                                      {d.title}
                                      {d.done && (d.completedBy || d.completedAt) && (
                                        <span className="text-[11px] text-emerald-600 ml-1.5">
                                          — {d.completedBy || "Project Manager"}
                                          {d.completedAt ? ` · ${new Date(d.completedAt).toLocaleDateString()}` : ""}
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {m.deliverySubmissions?.length > 0 && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">
                                Submitted files
                              </div>
                              <div className="space-y-1">
                                {m.deliverySubmissions.map((s: any, si: number) => (
                                  <div key={si} className="text-[12px] text-gray-600">
                                    <span className="font-semibold text-gray-700">Revision {s.revision}</span>
                                    {s.submittedBy ? ` · ${s.submittedBy}` : ""}
                                    {s.submittedAt ? ` · ${new Date(s.submittedAt).toLocaleDateString()}` : ""}
                                    {s.approvedForClient && (
                                      <span className="ml-1.5 px-1.5 py-px rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">
                                        Approved for client
                                      </span>
                                    )}
                                    {s.notes && <span className="block text-[11px] text-gray-500">{s.notes}</span>}
                                    {(s.files || []).map((f: any, fi: number) => (
                                      <a
                                        key={fi}
                                        href={f.url || "#"}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="block text-[11px] text-blue-600 hover:underline truncate"
                                      >
                                        {f.name}
                                      </a>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {m.rejectionHistory?.length > 0 && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider font-bold text-red-400 mb-1">
                                Rejection history
                              </div>
                              {m.rejectionHistory.map((r: any, ri: number) => (
                                <div key={ri} className="text-[11px] text-red-700">
                                  ✕ {r.note}
                                  <span className="text-red-400">
                                    {" "}— {r.byName || "BD"}
                                    {r.at ? ` · ${new Date(r.at).toLocaleDateString()}` : ""} (rev {r.revision})
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ERP Sync */}
          <div className="pt-3 border-t border-gray-100">
            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2 flex items-center gap-1.5"><Server size={12} /> ERP Sync</div>
            {project.isDraft ? (
              <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                This is a <strong>Draft</strong> — it can't be pushed to ERP. Resume the draft from the Projects list, complete the remaining information and click Create Project first.
              </p>
            ) : erp?.projectId ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-3">
                <Row label="ERP Project ID" value={erp.projectId} mono />
                <Row label="ERP Status" value={erp.status || "—"} />
                <Row label="Progress" value={`${erp.progress || 0}%`} />
                <Row label="Sync State" value={sync || "Synced"} />
              </div>
            ) : (
              <div className="space-y-2">
                {/* Only appears when there is a problem — a green bar above every
                    project would stop being read within a day. */}
                <ErpConnectionBanner onlyWhenProblem />
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-[12px] text-gray-500">Not yet submitted to ERP for operational review.</p>
                  <button
                    onClick={submitToErp}
                    disabled={erpBusy || erpBlocked}
                    title={erpBlocked ? "Waiting for the ERP connection" : ""}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {erpBusy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Submit to ERP
                  </button>
                </div>
              </div>
            )}

            {/* Held: nothing was sent, so this is not an error — it is a queue. */}
            {sync === "Held — ERP Unavailable" && (
              <div className="mt-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <strong>Waiting to be sent.</strong> The project is complete and saved here; it was not
                submitted because ERP could not be reached. Submit again once the connection is back.
              </div>
            )}

            {/* Uncertain: the dangerous one, so it is spelled out. */}
            {sync === "Sync Uncertain" && (
              <div className="mt-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
                <strong>ERP did not respond in time.</strong> We cannot confirm whether it received this
                project. Retrying is safe — Omni Workspace checks what ERP already holds before sending
                again, so this cannot create a duplicate.
              </div>
            )}

            {["Sync Failed", "Sync Uncertain", "Held — ERP Unavailable"].includes(sync || "") && (
              <button
                onClick={submitToErp}
                disabled={erpBusy || erpBlocked}
                className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-amber-700 font-semibold hover:underline disabled:opacity-50 disabled:no-underline"
              >
                <RefreshCw size={13} />
                {sync === "Sync Uncertain" ? "Check ERP and retry" : "Retry Sync"}
              </button>
            )}
          </div>
        </div>
      </div>

      {dialog && (
        <NoteDialog
          open
          title={dialog.title}
          description={dialog.description}
          placeholder={dialog.placeholder}
          confirmLabel={dialog.confirmLabel}
          tone={dialog.tone}
          onCancel={() => setDialog(null)}
          onConfirm={(note) => {
            const d = dialog;
            setDialog(null);
            if (d.kind === "checklist") acknowledgeItem(d.index, true, note);
            else reviewMilestone(d.index, "reject", note);
          }}
        />
      )}
    </div>
  );
};

export default ProjectViewModal;
